import * as React from 'react';
import { ACTION_SELECT_MAP_CARD } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AvailabilitySearchResultsMapDialog } from 'components/www/availability-search/AvailabilitySearchResultsMapDialog';
import { IconButton } from 'omakase';
import { IconClose } from 'ui/icons';
import { IconCrossLarge } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { SEARCH_SCROLL_KEY, sessionPrioritizedBackend } from 'utils/storage-backends/backends';
import { Theme, css } from '@emotion/react';
import { consumer } from '@tock/coretypes';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getInMobileNative } from 'selectors/appSelector';
import { lockSelector } from 'selectors/lockSelectors';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useExperienceCardAction } from 'hooks/search/useExperienceCardAction';
import { useFlatSearchQuery } from 'hooks/search/useFlatSearchQuery';
import { useLocation } from 'react-router';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import { useSearchResults } from 'hooks/search/useSearchResults';
import { useSelector } from 'react-redux';
import AvailabilitySearchMap from 'components/www/availability-search/AvailabilitySearchMap';
import AvailabilitySearchResultsMapResults from 'components/www/availability-search/AvailabilitySearchResultsMapResults';

const overall = css`
  display: flex;
  height: 100%;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10;
`;

const map = css`
  flex: 1;
`;

const actions =
  (inMobileNative: boolean) =>
  ({ spacing }: Theme) => css`
    position: absolute;
    top: ${inMobileNative ? 'env(safe-area-inset-top)' : spacing(2)};
    right: ${spacing(2)};
  `;

const close = ({ colors, shadow }: Theme) => css`
  background-color: ${colors.bgDefault};
  box-shadow: ${shadow[100]};
  &:hover {
    background-color: ${colors.bgLighter};
  }
`;

/**
 * Full map compnent wraps AvailabilitySearchMap and adds a close button,
 * a react-slick powered results list, and a dialog for starting a booking process
 */
const AvailabilitySearchResultsFullMap: React.FunctionComponent = () => {
  const metroAreaName = useCurrentMetroArea()?.name;
  const searchHandlers = useSearchHandlerContext();
  const flatSearchQuery = useFlatSearchQuery();
  const { result: searchResults } = useSearchResults();
  const lock = useSelector(lockSelector);
  const experienceCardAction = useExperienceCardAction(searchHandlers?.mode);
  const [result, setResult] = React.useState<number>();
  const [hasInteracted, setHasInteracted] = React.useState<boolean>(false);
  const location = useLocation();
  const inMobileNative = useSelector(getInMobileNative);
  const amplitudeSendEvent = useAmplitudeSendEvent();

  const { isFullMapView, closeFullMapView, setSelectedBusinessId, selectedBusinessId } = {
    ...searchHandlers,
  };

  const hasSelectedBusinessId = !!selectedBusinessId;

  React.useEffect(() => {
    if (isFullMapView && !hasSelectedBusinessId) {
      const firstBusinessId =
        searchResults &&
        searchResults.offeringAvailability &&
        searchResults.offeringAvailability.length > 0
          ? (searchResults.offeringAvailability[0].business?.id as number | undefined)
          : undefined;
      setSelectedBusinessId && setSelectedBusinessId(firstBusinessId);
    }
  }, [searchResults, isFullMapView, setSelectedBusinessId, hasSelectedBusinessId]);

  const handleMarkerClick = (businessId: number) => {
    sessionPrioritizedBackend.set(
      SEARCH_SCROLL_KEY,
      JSON.stringify({
        type: 'businessId',
        selectedBusinessId: businessId,
        key: `${location.pathname}:${location.search}`,
      })
    );
  };

  const handleSlideChange = (currentSlide: number) => {
    if (searchResults && searchResults.offeringAvailability.length > 0) {
      const currentSlideBusiness = searchResults.offeringAvailability[currentSlide].business;
      if (currentSlideBusiness) {
        setSelectedBusinessId && setSelectedBusinessId(currentSlideBusiness.id as number);
        setHasInteracted(true);
      }
    }
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    closeFullMapView && closeFullMapView();
    setHasInteracted(false);
  };

  const handleCloseResult = () => setResult(undefined);

  const handleClickBookNow = (result: consumer.ISearchOfferingBusiness) => {
    window.analytics.sendEvent(
      'Citywide Search',
      'Citywide Search result selected on mobile map',
      metroAreaName
    );
    const data = {
      searchContext: searchHandlers?.analyticsSearchContext,
    };
    amplitudeSendEvent(ACTION_SELECT_MAP_CARD, data);
    ga4SendEvent('select_map_card', data);
    setResult(selectedBusinessId);
  };

  const selectedBusiness =
    searchResults &&
    searchResults.offeringAvailability &&
    searchResults.offeringAvailability.length > 0
      ? searchResults.offeringAvailability.find((offeringAvailability) => {
          return offeringAvailability.business?.id === selectedBusinessId;
        })
      : undefined;

  if (!isFullMapView) {
    return null;
  }

  return (
    <div css={overall}>
      <div css={map}>
        <AvailabilitySearchMap
          hasControls={false}
          latOffsetFactor={0.15}
          gestureHandling="greedy"
          zoom={hasInteracted ? 14 : undefined}
          onMarkerClick={handleMarkerClick}
        />
      </div>
      <div css={actions(inMobileNative)}>
        <IconButton
          variant="ghost"
          size="medium"
          aria-label="Close"
          css={close}
          color="inherit"
          onClick={handleClose}
        >
          <IconMigrationUtil legacyIcon={<IconClose />} newIcon={<IconCrossLarge />} />
        </IconButton>
      </div>
      <AvailabilitySearchResultsMapResults
        selectedBusinessId={selectedBusinessId}
        results={searchResults}
        handleSlideChange={handleSlideChange}
        onBookNow={handleClickBookNow}
      />
      {selectedBusiness && searchHandlers?.mode ? (
        <AvailabilitySearchResultsMapDialog
          businessResult={selectedBusiness as consumer.SearchOfferingBusiness}
          open={!!result}
          onClose={handleCloseResult}
          experienceCardAction={experienceCardAction}
          lock={lock}
          query={flatSearchQuery}
          stateValue={searchHandlers?.mode}
        />
      ) : null}
    </div>
  );
};

export default AvailabilitySearchResultsFullMap;
